import { useContext, useSyncExternalStore } from "react";
import type { DialogueActions, DialogueState, Listener } from "@/store/types";
import { DialogContext } from "./DialogContext";

/**
 * 使用对话状态和动作
 * @returns 对话状态和动作
 */
export const useDialog = (): {
  state: DialogueState;
  actions: DialogueActions;
} => {
  const store = useContext(DialogContext);

  if (!store) {
    throw new Error("useDialog must be used within DialogProvider");
  }

  // 使用 useSyncExternalStore 订阅状态变化
  const state = useSyncExternalStore(
    (callback: Listener) => store.subscribe(callback),
    () => store.getState(),
    () => store.getState()
  );

  const actions: DialogueActions = {
    startTypewriter: store.startTypewriter,
    clearTypewriter: store.clearTypewriter,
    handleClick: store.handleClick,
    nextDialogue: store.nextDialogue,
    showDialogue: store.showDialogue,
    hideDialogue: store.hideDialogue,
    queueDialogues: store.queueDialogues,
    setOnComplete: store.setOnComplete,
    startDialogue: store.startDialogue,
    setTypewriterSpeed: store.setTypewriterSpeed,
    setEffectRepeats: store.setEffectRepeats,
  };

  return { state, actions };
};

/**
 * 仅使用对话状态
 * @returns 对话状态
 */
export const useDialogState = (): DialogueState => {
  const { state } = useDialog();
  return state;
};

/**
 * 仅使用对话动作
 * @returns 对话动作
 */
export const useDialogActions = (): DialogueActions => {
  const { actions } = useDialog();
  return actions;
};
